import { useLayout } from '@/layout/LayoutProvider';
import { Select as RebrandedSelect } from '@storeblocks/rebrand/select';
import { Select } from '@storeblocks/select';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { Label } from '../../shared/components/Label/Label';
import { FieldProps, StringSchema } from '../types';
import { joinErrors, zip } from '../validation/utils';
import { hasErrors } from '../validation/validateSchema';

type SelectFieldProps = FieldProps<StringSchema, string | null>;

export const SelectField = ({
  schema,
  name: fieldName,
  value: fieldValue,
  touched,
  errors,
  id,
  onBlur,
  onChange,
  forceShowError,
  readOnly
}: SelectFieldProps) => {
  const { getText } = useTranslations();
  const $uiOptions = schema.$uiOptions || {};
  const showError = !!((touched || forceShowError) && hasErrors(errors));
  const { isRebrand } = useLayout();

  const handleBlur = () => {
    return onBlur({
      target: {
        name: fieldName,
        touched: true
      }
    });
  };

  const handleChange = (e: any) => {
    const { name } = e.target;
    const { value } = e.target;

    onChange({
      target: {
        name,
        value
      }
    });
  };

  const error = showError ? joinErrors(errors, getText) : undefined;

  const emptySelectionOptional =
    $uiOptions.emptySelectOption === undefined
      ? ''
      : $uiOptions.emptySelectOption;

  const options = [
    ['', emptySelectionOptional],
    ...zip(schema.enum!, schema.enumNames!)
  ].map(([value, label]) => {
    return {
      label: label!,
      value: value!
    };
  });

  const useDefaultSize = !$uiOptions.fieldSize;

  const label = (
    <Label title={schema.title} description={schema.description} id={id} />
  );

  return isRebrand ? (
    <RebrandedSelect
      id={id}
      name={fieldName}
      label={schema.title ?? ''}
      hint={$uiOptions.hint}
      onChange={handleChange}
      value={fieldValue ?? undefined}
      options={options}
      disabled={readOnly}
    />
  ) : (
    <Select
      id={id}
      name={fieldName}
      label={label as any}
      inputProps={{
        autoFocus: $uiOptions.autoFocus,
        'aria-describedby': schema.description ? `${id}_description` : undefined
      }}
      small={useDefaultSize || $uiOptions.fieldSize === 'small'}
      medium={$uiOptions.fieldSize === 'medium'}
      large={$uiOptions.fieldSize === 'large'}
      error={error}
      hint={$uiOptions.hint}
      value={fieldValue || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      options={options}
      disabled={readOnly}
    />
  );
};
